import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { unlockAudio } from "../lib/sound";
import { buzz } from "../lib/haptics";
import { subscribeHeroProgress, getHeroProgress } from "../lib/heroLoader";
import ShaderFX from "./ShaderFX";

const LOGO = `${process.env.PUBLIC_URL || ""}/assets/mask-logo.png`;

function hasConsent() {
  if (typeof window === "undefined") return false;
  if (localStorage.getItem("toji_age_ok")) return true;
  return document.cookie.includes("toji_age_ok=1");
}

function setConsent() {
  localStorage.setItem("toji_age_ok", "1");
  document.cookie = `toji_age_ok=1; max-age=${60 * 60 * 24 * 365}; path=/; SameSite=Lax`;
}

function forceShow() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("agegate");
}

const BRAND = "TOJI PEPTIDES".split("");

export default function AgeGate() {
  // phase: load -> (gate | cut) -> gone
  const [phase, setPhase] = useState("load");
  const [pct, setPct] = useState(() => Math.round(getHeroProgress() * 100));
  const finished = useRef(false);

  // Parallax source (pointer + device tilt), smoothed
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const mx = useSpring(px, { stiffness: 45, damping: 18, mass: 0.7 });
  const my = useSpring(py, { stiffness: 45, damping: 18, mass: 0.7 });

  // Layered depths — "powerful" parallax (large displacement)
  const smokeX = useTransform(mx, (v) => v * 55);
  const smokeY = useTransform(my, (v) => v * 55);
  const kanjiX = useTransform(mx, (v) => v * 120);
  const kanjiY = useTransform(my, (v) => v * 95);
  const kanjiR = useTransform(mx, (v) => v * 6);
  const contentX = useTransform(mx, (v) => v * -34);
  const contentY = useTransform(my, (v) => v * -26);
  const glowX = useTransform(mx, (v) => v * 80);
  const glowY = useTransform(my, (v) => v * 80);

  useEffect(() => {
    if (window.__lenis) window.__lenis.stop();
    const onMove = (e) => {
      px.set(e.clientX / window.innerWidth - 0.5);
      py.set(e.clientY / window.innerHeight - 0.5);
    };
    const onTilt = (e) => {
      if (e.gamma == null || e.beta == null) return;
      px.set(Math.max(-0.5, Math.min(0.5, e.gamma / 45)));
      py.set(Math.max(-0.5, Math.min(0.5, (e.beta - 45) / 45)));
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("deviceorientation", onTilt, true);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("deviceorientation", onTilt, true);
    };
    // eslint-disable-next-line
  }, []);

  // Real hero-frame preload progress
  useEffect(() => {
    setPct(Math.round(getHeroProgress() * 100));
    return subscribeHeroProgress((p) => setPct(Math.round(p * 100)));
  }, []);

  // When forging completes: returning users cut straight through, new users see the gate
  useEffect(() => {
    if (pct < 100 || finished.current) return;
    finished.current = true;
    const returning = hasConsent() && !forceShow();
    if (returning) {
      setTimeout(() => setPhase("cut"), 300);
      setTimeout(() => {
        if (window.__lenis) window.__lenis.start();
        setPhase("gone");
      }, 1150);
    } else {
      setTimeout(() => setPhase("gate"), 450);
    }
  }, [pct]);

  const enter = () => {
    buzz([18, 40, 22]);
    setConsent();
    unlockAudio();
    setPhase("cut");
    setTimeout(() => {
      if (window.__lenis) window.__lenis.start();
      setPhase("gone");
    }, 850);
  };

  const leave = () => {
    window.location.href = "https://www.google.com";
  };

  return (
    <AnimatePresence>
      {phase !== "gone" && (
        <motion.div
          data-testid="age-gate"
          exit={{ opacity: 0, filter: "blur(8px)", scale: 1.03 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[120] flex items-center justify-center overflow-hidden bg-[#050505] px-6"
        >
          {/* Layer 0 — flowing sumi-ink smoke (parallax) */}
          <motion.div
            style={{ x: smokeX, y: smokeY }}
            className="pointer-events-none absolute -inset-[16%] z-0"
          >
            <ShaderFX variant="ink" intensity={1.25} className="h-full w-full" />
          </motion.div>

          {/* Ambient forge glow (parallax) */}
          <motion.div
            style={{ x: glowX, y: glowY }}
            className="pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2"
          >
            <motion.div
              animate={{ opacity: [0.08, 0.15, 0.08], scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
              className="h-[70vh] w-[70vh] rounded-full bg-[#d91616] blur-[160px]"
            />
          </motion.div>

          {/* Layer 1 — giant kanji watermark (strongest parallax) */}
          <motion.span
            style={{ x: kanjiX, y: kanjiY, rotate: kanjiR }}
            initial={{ opacity: 0, scale: 1.15 }}
            animate={{ opacity: 0.05, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="font-jp pointer-events-none absolute z-[5] select-none text-[46vh] leading-none text-white"
          >
            峠
          </motion.span>

          {/* Layer 2 — content (subtle counter-parallax for depth) */}
          <motion.div
            style={{ x: contentX, y: contentY }}
            animate={phase === "cut" ? { opacity: 0, scale: 1.04 } : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="relative z-10 flex w-full max-w-lg flex-col items-center text-center"
          >
            {/* Persistent mask */}
            <motion.img
              src={LOGO}
              alt=""
              initial={{ opacity: 0, scale: 0.7, filter: "blur(12px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="h-24 w-24 object-contain drop-shadow-[0_0_40px_rgba(217,22,22,0.5)] md:h-28 md:w-28"
            />

            {/* Persistent brand */}
            <div className="mt-6 flex overflow-hidden">
              {BRAND.map((c, i) => (
                <motion.span
                  key={`${c}-${i}`}
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.045, ease: [0.16, 1, 0.3, 1] }}
                  className="font-mono2 text-[11px] uppercase tracking-[0.5em] text-[#d91616] md:text-xs"
                >
                  {c === " " ? "\u00A0" : c}
                </motion.span>
              ))}
            </div>

            {/* Swappable region: forging bar OR gate content */}
            <div className="mt-10 w-full">
              <AnimatePresence mode="wait">
                {phase === "gate" ? (
                  <motion.div
                    key="gate"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col items-center"
                  >
                    <h1 className="font-display text-5xl font-black leading-[0.9] tracking-tighter text-white md:text-6xl">
                      19<span className="text-[#d91616]">+</span> ONLY
                    </h1>
                    <p className="mt-6 max-w-md font-body text-sm leading-relaxed text-white/55 md:text-base">
                      You must be 19 years or older to enter. Toji Peptides is an
                      educational resource — all content is for informational and
                      research purposes only, not medical advice.
                    </p>

                    <div className="mt-10 flex w-full flex-col items-center gap-4">
                      <button
                        data-testid="age-gate-enter"
                        onClick={enter}
                        className="slash-host group relative w-full max-w-xs overflow-hidden border border-[#d91616] bg-[#d91616] py-4 font-mono2 text-xs uppercase tracking-[0.3em] text-white transition-colors hover:bg-transparent hover:text-[#d91616]"
                      >
                        I am 19 or older — Enter
                        <span className="pulse-bloom z-0" aria-hidden="true" />
                        <span className="toji-slash" aria-hidden="true" />
                        <span className="toji-slash thin" aria-hidden="true" />
                      </button>
                      <button
                        data-testid="age-gate-leave"
                        onClick={leave}
                        className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/35 transition-colors hover:text-white/70"
                      >
                        I am under 19 — Leave
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="load"
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.4 }}
                    className="mx-auto flex w-[260px] flex-col gap-2 md:w-[320px]"
                  >
                    <div className="relative h-[2px] w-full overflow-hidden bg-white/10">
                      <motion.div
                        className="absolute inset-y-0 left-0 bg-[#d91616] shadow-[0_0_12px_rgba(217,22,22,0.9)]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                      <span>Forging</span>
                      <span data-testid="age-gate-pct">{pct}%</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Katana cut + light pulse on exit */}
          <AnimatePresence>
            {phase === "cut" && (
              <>
                <motion.div
                  key="burst"
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{ opacity: [0, 0.9, 0], scale: 1.6 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="pointer-events-none absolute left-1/2 top-1/2 z-20 h-[60vh] w-[60vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.95)_0%,rgba(255,60,60,0.6)_35%,rgba(217,22,22,0)_70%)]"
                />
                <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center overflow-hidden">
                  <motion.div
                    key="slash"
                    initial={{ x: "-130%", opacity: 0 }}
                    animate={{ x: "130%", opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 0.6, ease: [0.5, 0, 0.2, 1] }}
                    style={{ rotate: -22 }}
                    className="h-[3px] w-[170vw] bg-gradient-to-r from-transparent via-white to-[#ff2d2d]"
                  >
                    <span className="absolute -top-2 left-1/2 h-5 w-5 -translate-x-1/2 rounded-full bg-white shadow-[0_0_30px_14px_rgba(255,60,60,0.9)]" />
                  </motion.div>
                </div>
              </>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
